import React, { useEffect, useState } from "react";
import { getLocationAnalytics } from "../../api/context/analytics";
import { FiInfo, FiRefreshCw } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import GoogleMap from "google-map-react";
import { GOOGLE_MAP_API_KEY } from "../../utils/config"; 

const LocationMarker = ({ location, visits, intensity }) => {
    const size = 28 + (intensity * 34);
    const markerColor = intensity > 0.65 ? "#dc2626" : intensity > 0.35 ? "#f97316" : "#2563eb";

    return (
        <div
            className="google-location-marker"
            title={`${location} - ${visits} visits`}
            style={{
                width: size,
                height: size,
                backgroundColor: intensity > 0.65 ? "rgba(220, 38, 38, 0.28)" : intensity > 0.35 ? "rgba(249, 115, 22, 0.24)" : "rgba(37, 99, 235, 0.24)",
                borderColor: intensity > 0.65 ? "rgba(185, 28, 28, 0.7)" : intensity > 0.35 ? "rgba(234, 88, 12, 0.66)" : "rgba(29, 78, 216, 0.66)",
                boxShadow: `0 0 0 ${8 + intensity * 16}px ${intensity > 0.65 ? "rgba(220, 38, 38, 0.14)" : intensity > 0.35 ? "rgba(249, 115, 22, 0.12)" : "rgba(37, 99, 235, 0.12)"}`,
            }}
        >
            <span style={{ backgroundColor: markerColor }}>{visits}</span>
        </div>
    );
};

const LocationAnalytics = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());

    const getTodayDate = () => new Date().toISOString().split('T')[0];
    const toArray = (value) => Array.isArray(value) ? value : [];
    const toNumber = (value) => Number(value || 0);

    const locationRows = toArray(analytics?.locations?.top); 

    const getLocationName = (item) =>
        [item?.city].filter(Boolean).join(",") || "Unknown";

    const getLocationCount = (item) =>
        toNumber(item?.visits || item?.visitCount || item?.totalVisits || item?.totalEvents || item?.count);

    const sortedLocations = [...locationRows].sort(
        (a, b) => getLocationCount(b) - getLocationCount(a)
    );

    const mapLocations = sortedLocations.filter((item) =>
        Number.isFinite(Number(item?.approxLatitude)) && Number.isFinite(Number(item?.approxLongitude))
    );

    const mapCenter = {
        lat: Number(analytics?.geographicBounds?.centerLatitude) || Number(mapLocations[0]?.approxLatitude) || 20.5937,
        lng: Number(analytics?.geographicBounds?.centerLongitude) || Number(mapLocations[0]?.approxLongitude) || 78.9629,
    };

    const highestVisitCount = Math.max(...mapLocations.map(getLocationCount), 1);

    const fetchAnalytics = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const requestBody = {
            limit: 15,
            startDate: filters.startDate,
            endDate: filters.endDate
        };

        try {
            await new Promise((resolve, reject) => {
                getLocationAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);
                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject();
                    }
                });
            });
        } catch (error) {
            setAnalytics(null);
            toast.error('Failed to fetch location analytics data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate') {
            if (value > today) return toast.error('Start date cannot be in the future');
            if (filters.endDate && value > filters.endDate) return toast.error('Start date cannot be after end date');
        }

        if (key === 'endDate') {
            if (value > today) return toast.error('End date cannot be in the future');
            if (filters.startDate && value < filters.startDate) return toast.error('End date cannot be before start date');
        }

        setFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => fetchAnalytics(true), 500);
            return () => clearTimeout(timer);
        }
    }, [filters]);

    const handleClearFilters = () => {
        const defaults = getDefaultDates();
        setFilters(defaults);
        setTimeout(() => fetchAnalytics(true), 100);
    };

    const isFilterActive = () => {
        const defaults = getDefaultDates();
        return filters.startDate !== defaults.startDate || filters.endDate !== defaults.endDate;
    };

    const isLoadingInitial = loading && !refreshing;
    const hasNoData = !loading && !analytics;

    return (
        <div className="container-fluid p-4 min-vh-100">

            {/* HEADER WITH FILTERS + REFRESH */}
            <div
                className="d-flex justify-content-between align-items-center"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1000,
                    background: '#f9fafb',
                    margin: '-44px -48px 20px',
                    padding: '40px 48px 32px',
                }}
            >
                <div className="d-flex align-items-center mb-0">
                    <div
                        onClick={() => navigate(-1)}
                        style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center', marginRight: '15px' }}
                    >
                        <IoMdArrowBack className='icon' />
                    </div>
                    <h4 className="mb-0 fw-bold">Location Analytics</h4>
                </div>

                <div className="d-flex align-items-center gap-3">

                    {/* DATE FILTERS */}
                    <div className="d-flex align-items-center gap-2">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            style={{ width: '140px' }}
                            max={filters.endDate || getTodayDate()}
                        />

                        <div className="text-muted small">to</div>

                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            style={{ width: '140px' }}
                            min={filters.startDate || undefined}
                            max={getTodayDate()}
                        />

                        {isFilterActive() && (
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-sm btn-outline-secondary"
                                disabled={refreshing}
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {/* REFRESH BUTTON */}
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn border border-secondary bg-transparent rounded-3 d-flex align-items-center gap-2 px-3"
                        style={{ height: 40 }}
                        disabled={refreshing}
                    >
                        <FiRefreshCw
                            className="text-secondary"
                            size={18}
                            style={{
                                animation: refreshing ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                        <span className="text-secondary">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                    </button>
                </div>
            </div>

            {isLoadingInitial ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                    <Loader />
                </div>
            ) : hasNoData ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted" style={{ minHeight: 400 }}>
                    <FiInfo size={48} className="mb-3 text-secondary" />
                    <h5 className="fw-bold mb-2">No Data Available</h5>
                    <p className="mb-0" style={{ maxWidth: 420 }}>
                        We couldn't find any location analytics for the selected period.
                    </p>
                </div>
            ) : (
              <>
            {/* MAP + PANEL */}
            <div className="row g-4">
                <div className="col-md-8">
                    <div className="location-map-shell">
                        <GoogleMap bootstrapURLKeys={{ key: GOOGLE_MAP_API_KEY }} center={mapCenter} defaultZoom={5}>
                            {mapLocations.map((location, index) => {
                                const visits = getLocationCount(location);
                                return (
                                    <LocationMarker
                                        key={index}
                                        lat={Number(location.approxLatitude)}
                                        lng={Number(location.approxLongitude)}
                                        location={getLocationName(location)}
                                        visits={visits}
                                        intensity={visits / highestVisitCount}
                                    />
                                );
                            })}
                        </GoogleMap>
                    </div>
                </div>

                <div className="col-md-4">
                    <div
                        className="border rounded-3"
                        style={{
                            height: 'calc(70vh)',
                            minHeight: '420px',
                            overflowY: 'auto',
                            background: '#f8f9fa'
                        }}
                    >
                        <div className="p-3">
                            <h6 className="text-center fw-bold mb-3">Top Cities</h6>

                            <div className="d-flex flex-column gap-2">
                                {sortedLocations.map((loc, index) => {
                                    const name = getLocationName(loc);
                                    const count = getLocationCount(loc);

                                    return (
                                        <div key={index} className="d-flex justify-content-between align-items-center p-2 rounded-2" style={{ backgroundColor: 'white', border: '1px solid #f1f1f1' }}>
                                            <div className="d-flex align-items-center">
                                                <span className="badge bg-light text-dark me-2">#{index + 1}</span>
                                                <span className="fw-medium text-truncate" style={{ maxWidth: '140px' }} title={name}>{name}</span>
                                            </div>
                                            <span className="fw-bold" style={{ color: '#2563eb' }}>{count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
              </>
            )}

            <style>{`
                .location-map-shell {
                    position: relative;
                    height: calc(70vh);
                    min-height: 420px;
                    overflow: hidden;
                    border: 1px solid #e3e7eb;
                    border-radius: 8px;
                    background: #f8fafc;
                }
                .google-location-marker {
                    position: absolute;
                    transform: translate(-50%, -50%);
                    border-radius: 50%;
                    border: 1px solid;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
                .google-location-marker span {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 11px;
                    font-weight: 700;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default LocationAnalytics;
